import { showToast } from '../Common/Toast';
import React, { useState, useEffect } from 'react';
import { useUser } from '../../contexts/UserContext';
import { supabase } from '../../utils/supabaseClient';
import { toCanonicalPaymentTerms } from '../../utils/paymentTerms';
import PaymentTermsSelect from '../Common/PaymentTermsSelect';
import { DEFAULT_TAX_RATE_MAX, DEFAULT_TAX_RATE_RANGE_TEXT, defaultTaxRateError } from '../../utils/defaultTaxRate';
import {
  CurrencyDollarIcon,
  ReceiptPercentIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';


// Native <select> popups are drawn by the OS, so they ignore the app's dark
// glassmorphism unless told: color-scheme dark themes the popup itself, and the
// option class themes each row. Without both, the wizard's dropdowns opened as a
// bright white list against a dark form. Same pair QuoteBuilder uses.
const selectOptionClass = 'bg-slate-800 text-gray-100';

/**
 * Board #2837 — invoice/quote numbering validation.
 *
 * These drive a legal and accounting identifier, so "anything the user typed" is not an
 * acceptable input contract. A prefix is a short document code, not free text: letters,
 * digits and dashes, upper-cased because that is how every document in this app renders
 * it (INV-2026-0001), and bounded so it cannot run off a PDF header.
 *
 * Collision with an existing invoice number — which the brief also asks for — is NOT
 * checked here and does not need to be: this is the ONBOARDING step, so the company has
 * no invoices yet by construction. The Settings > Invoicing tab, which is where the
 * number can be changed once invoices exist, already refuses to regress below the highest
 * issued number (its noRegress guard).
 */
const PREFIX_MAX = 10;

const sanitizeNumberPrefix = (raw) =>
  String(raw ?? '').toUpperCase().replace(/[^A-Z0-9-]/g, '').slice(0, PREFIX_MAX);

// `parseInt(v) || 1001` turned a typed 0 into 1001 and an empty box into 1001 too, so the
// field fought the user mid-edit. Empty stays empty; anything below 1 clamps to 1.
const sanitizeNextNumber = (raw) => {
  if (raw === '' || raw === null || raw === undefined) return '';
  const n = parseInt(raw, 10);
  if (Number.isNaN(n)) return '';
  return Math.max(1, n);
};

const FinancialSetupStep = ({ onComplete, onValidationChange, registerSave }) => {
  const { user } = useUser();
  const [financialSettings, setFinancialSettings] = useState({
    default_tax_rate: 0,
    default_payment_terms: 'net_30',
    late_fee_rate: 1.5,
    invoice_prefix: 'INV',
    quote_number_prefix: 'QUO',
    next_invoice_number: 1001,
    next_quote_number: 1001,
    labor_rate: 75.00
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // #5943: one picker for every terms field (PaymentTermsSelect, utils/paymentTerms). The loaded
  // value is normalized below, so the select shows what is saved and saves the canonical code.

  // Load existing financial settings
  useEffect(() => {
    loadFinancialSettings();
  }, [user?.company_id]);

  const loadFinancialSettings = async () => {
    if (!user?.company_id) return;

    try {
      setLoading(true);

      const { data, error } = await supabase
        .from('company_settings')
        .select('*')
        .eq('company_id', user.company_id)
        .single();

      if (error && error.code !== 'PGRST116') throw error;

      if (data) {
        setFinancialSettings(prev => ({
          ...prev,
          default_tax_rate: data.default_tax_rate ?? prev.default_tax_rate,
          default_payment_terms: toCanonicalPaymentTerms(data.default_payment_terms) || prev.default_payment_terms,
          late_fee_rate: data.late_fee_rate ?? prev.late_fee_rate,
          invoice_prefix: data.invoice_number_prefix || prev.invoice_prefix,
          quote_number_prefix: data.quote_number_prefix || prev.quote_number_prefix,
          next_invoice_number: data.next_invoice_number || prev.next_invoice_number,
          next_quote_number: data.next_quote_number || prev.next_quote_number,
          labor_rate: data.labor_rate ?? prev.labor_rate
        }));
      }
    } catch (error) {
      console.error('Error loading financial settings:', error);
    } finally {
      setLoading(false);
    }
  };

  // Validate financial settings
  // A labor rate this high is a typo, not a business decision. It is the hourly rate that
  // seeds quotes and invoices, so a fat-finger here is a money-path defect, not a cosmetic
  // one. Measured live 2026-08-29: one company holds 5095.00 while every other rate on the
  // platform is 165.00 or less (95.00 is the most common, 43 companies) — 5095 is "95" with
  // a slip. LABOR_RATE_MAX is deliberately generous: specialty and emergency trades really
  // do bill several hundred an hour, so this blocks the absurd rather than the unusual.
  const LABOR_RATE_MAX = 1000;
  const LABOR_RATE_HIGH = 500;

  const validateFinancialSettings = () => {
    const warnings = [];
    const errors = [];

    if (financialSettings.default_tax_rate === 0) {
      warnings.push('Consider setting a tax rate if your services are taxable');
    }

    const taxError = defaultTaxRateError(financialSettings.default_tax_rate);
    if (taxError) {
      errors.push(taxError);
    }

    const laborRate = financialSettings.labor_rate;
    if (laborRate !== '' && laborRate !== null && laborRate !== undefined) {
      if (laborRate < 0) {
        errors.push('Labor rate cannot be negative.');
      } else if (laborRate > LABOR_RATE_MAX) {
        // An ERROR, not a warning: the step only had a low-side warning and returned
        // valid:true unconditionally, so nothing on this screen could stop a bad rate
        // reaching company_settings.
        errors.push(`Labor rate of $${Number(laborRate).toLocaleString()}/hour looks like a typo. Enter an hourly rate up to $${LABOR_RATE_MAX.toLocaleString()}.`);
      } else if (laborRate > LABOR_RATE_HIGH) {
        warnings.push(`$${Number(laborRate).toLocaleString()}/hour is unusually high — double-check it is the HOURLY rate, not a day rate or a job total.`);
      } else if (laborRate < 50) {
        warnings.push('Labor rate seems low - make sure you\'re pricing competitively');
      }
    }

    onValidationChange?.({
      valid: errors.length === 0,
      errors,
      warnings
    });

    return errors.length === 0;
  };

  // Validate on settings change
  useEffect(() => {
    if (!loading) {
      validateFinancialSettings();
    }
  }, [financialSettings, loading]);

  // Update setting
  const updateSetting = (field, value) => {
    setFinancialSettings(prev => ({ ...prev, [field]: value }));
  };

  // Save financial settings — explicit payload using real company_settings columns.
  // Previously spread ...financialSettings which sent Potemkin fields (tax_name,
  // accept_cash, etc.) and tripped PGRST204 because those columns don't exist.
  const saveFinancialSettings = async () => {
    if (!user?.company_id) return;
    // The errors above were reported but never enforced: the wizard only disables Continue
    // on validation for steps 1-2, and this save did not check, so a refused value was
    // written anyway. Both the footer Continue and the in-form Save come through here.
    if (!validateFinancialSettings()) return;

    try {
      setSaving(true);

      const payload = {
        // A cleared field is empty, not zero — coerce at the boundary so an empty
        // input never reaches a numeric column (and a deliberate 0% tax survives).
        default_tax_rate: Number(financialSettings.default_tax_rate) || 0,
        default_payment_terms: financialSettings.default_payment_terms,
        invoice_number_prefix: financialSettings.invoice_prefix,
        quote_number_prefix: financialSettings.quote_number_prefix,
        next_invoice_number: financialSettings.next_invoice_number,
        next_quote_number: financialSettings.next_quote_number,
        labor_rate: Number(financialSettings.labor_rate) || 0,
        updated_at: new Date().toISOString()
      };

      const { error } = await supabase
        .from('company_settings')
        .update(payload)
        .eq('company_id', user.company_id);

      if (error) throw error;

      onComplete?.(financialSettings);
    } catch (error) {
      console.error('Error saving financial settings:', error);
      showToast('error', 'Failed to save financial settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Let the wizard's footer "Continue" button save this step too (not just the
  // in-form "Save Financial Settings & Continue" button).
  //
  // Do NOT wrap saveFinancialSettings in useCallback to "fix" the dep array. OnboardingWizard
  // clears stepSaveRef on every step change (OnboardingWizard.js:428) and React runs CHILD
  // effects before PARENT effects, so a stable callback registers once and is then nulled by the
  // parent — footer-Continue silently stops saving. A fresh identity each render is what makes
  // this re-register on the following render. Same note in CompanyBasicsStep.js and
  // BusinessPreferencesStep.js.
  useEffect(() => { registerSave?.(saveFinancialSettings); }, [registerSave, saveFinancialSettings]);

  if (loading) {
    return (
      <div className="text-center py-12 [color-scheme:dark]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto mb-4"></div>
        <p className="text-gray-600">Loading financial settings...</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="text-center mb-8">
        <CurrencyDollarIcon className="w-12 h-12 text-indigo-600 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Financial Setup</h2>
        <p className="text-gray-600">
          Configure your tax rate, payment terms, and pricing structure for quotes and invoices.
        </p>
      </div>

      <div className="space-y-6">
        {/* Tax Settings */}
        <div className="bg-gradient-to-br from-white/80 dark:from-slate-900/80 to-blue-50/40 dark:to-blue-900/20 backdrop-blur-xl rounded-lg shadow-2xl border border-blue-200/50 dark:border-blue-900/50-sm border p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
            <ReceiptPercentIcon className="w-5 h-5 text-indigo-600" />
            Tax Settings
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Default Tax Rate (%)
              </label>
              {/* 0-20 inclusive, operator ruling 2026-09-24 (utils/defaultTaxRate). A value
                  above 20 stays as typed and is refused on save with the message below --
                  it used to be clamped here, which stored a rate nobody chose. */}
              <input
                type="number"
                value={financialSettings.default_tax_rate ?? ''}
                onChange={(e) => updateSetting(
                  'default_tax_rate',
                  e.target.value === '' ? '' : parseFloat(e.target.value)
                )}
                className="w-full px-3 py-2 border rounded-lg bg-white/70 dark:bg-slate-800/60 border-white/20 dark:border-slate-700/40 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                min="0"
                max={DEFAULT_TAX_RATE_MAX}
                step="0.01"
                placeholder="0.00"
                aria-invalid={!!defaultTaxRateError(financialSettings.default_tax_rate)}
                aria-describedby="financial-tax-rate-help"
              />
              <p id="financial-tax-rate-help" className="text-xs text-gray-500 mt-1">
                {DEFAULT_TAX_RATE_RANGE_TEXT}
              </p>
              {defaultTaxRateError(financialSettings.default_tax_rate) && (
                <p role="alert" className="mt-1 text-xs text-red-400">
                  {defaultTaxRateError(financialSettings.default_tax_rate)}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Payment Terms */}
        <div className="bg-gradient-to-br from-white/80 dark:from-slate-900/80 to-blue-50/40 dark:to-blue-900/20 backdrop-blur-xl rounded-lg shadow-2xl border border-blue-200/50 dark:border-blue-900/50-sm border p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Payment Terms</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Default Payment Terms
              </label>
              <PaymentTermsSelect
                value={financialSettings.default_payment_terms}
                onChange={(code) => updateSetting('default_payment_terms', code)}
                optionClassName={selectOptionClass}
                className="w-full px-3 py-2 border rounded-lg bg-white/70 dark:bg-slate-800/60 border-white/20 dark:border-slate-700/40 focus:outline-none focus:ring-2 focus:ring-indigo-500 [color-scheme:dark]"
              />
            </div>

            {/* Late fees are not built end-to-end. Nothing reads late_fee_rate — no app
                code, no DB function — and invoices.late_fee_amount is 0 on all 91 invoices
                (verified live 2026-08-28). Asking for a percentage that never charges
                anyone is worse than not asking: it reads as configured. Settings already
                replaced its late-fee controls with the same honest placeholder
                (InvoicingSettingsTab, audit settings-payment C3); this was the last
                surface still collecting it. The column is left alone — dropping one is a
                hard stop — so any value already saved survives for when the feature ships. */}
            <div className="rounded-lg border border-white/20 dark:border-slate-700/40 bg-white/5 dark:bg-slate-800/20 p-4">
              <div className="text-sm font-medium text-gray-900 dark:text-slate-200">Late Fees</div>
              <div className="text-xs text-gray-500 dark:text-slate-400 mt-1">Coming soon — not yet available.</div>
            </div>
          </div>
        </div>

        {/* Invoice & Quote Numbering */}
        <div className="bg-gradient-to-br from-white/80 dark:from-slate-900/80 to-blue-50/40 dark:to-blue-900/20 backdrop-blur-xl rounded-lg shadow-2xl border border-blue-200/50 dark:border-blue-900/50-sm border p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Invoice & Quote Numbering</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Invoice Prefix
              </label>
              {/* Board #2837: these two drive a legal and accounting identifier, and both
                  took anything. The prefix accepted any characters at any length; the
                  number used `parseInt(v) || 1001`, so a typed 0 silently became 1001 —
                  the falsy-zero trap that has bitten this repo before. */}
              <input
                type="text"
                value={financialSettings.invoice_prefix}
                onChange={(e) => updateSetting('invoice_prefix', sanitizeNumberPrefix(e.target.value))}
                className="w-full px-3 py-2 border rounded-lg bg-white/70 dark:bg-slate-800/60 border-white/20 dark:border-slate-700/40 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="INV"
                maxLength={PREFIX_MAX}
                aria-describedby="invoice-prefix-help"
              />
              <p id="invoice-prefix-help" className="mt-1 text-xs text-gray-500">
                Letters, numbers and dashes, up to {PREFIX_MAX} characters.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Next Invoice #
              </label>
              <input
                type="number"
                value={financialSettings.next_invoice_number}
                onChange={(e) => updateSetting('next_invoice_number', sanitizeNextNumber(e.target.value))}
                className="w-full px-3 py-2 border rounded-lg bg-white/70 dark:bg-slate-800/60 border-white/20 dark:border-slate-700/40 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                min="1"
                step="1"
              />
              <p className="mt-1 text-xs text-gray-500">
                A whole number, 1 or greater.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Quote Prefix
              </label>
              {/* Board #2837 named the invoice fields; the quote pair beside them had the
                  identical defect, so both are fixed rather than one. */}
              <input
                type="text"
                value={financialSettings.quote_number_prefix}
                onChange={(e) => updateSetting('quote_number_prefix', sanitizeNumberPrefix(e.target.value))}
                className="w-full px-3 py-2 border rounded-lg bg-white/70 dark:bg-slate-800/60 border-white/20 dark:border-slate-700/40 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="QUO"
                maxLength={PREFIX_MAX}
              />
              <p className="mt-1 text-xs text-gray-500">
                Letters, numbers and dashes, up to {PREFIX_MAX} characters.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Next Quote #
              </label>
              <input
                type="number"
                value={financialSettings.next_quote_number}
                onChange={(e) => updateSetting('next_quote_number', sanitizeNextNumber(e.target.value))}
                className="w-full px-3 py-2 border rounded-lg bg-white/70 dark:bg-slate-800/60 border-white/20 dark:border-slate-700/40 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                min="1"
                step="1"
              />
              <p className="mt-1 text-xs text-gray-500">
                A whole number, 1 or greater.
              </p>
            </div>
          </div>
        </div>

        {/* Pricing */}
        <div className="bg-gradient-to-br from-white/80 dark:from-slate-900/80 to-blue-50/40 dark:to-blue-900/20 backdrop-blur-xl rounded-lg shadow-2xl border border-blue-200/50 dark:border-blue-900/50-sm border p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Default Pricing</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Default Labor Rate ($/hour)
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <span className="text-gray-500 sm:text-sm">$</span>
                </div>
                <input
                  type="number"
                  value={financialSettings.labor_rate ?? ''}
                  onChange={(e) => updateSetting('labor_rate', e.target.value === '' ? '' : (parseFloat(e.target.value) || 0))}
                  className="w-full pl-7 pr-3 py-2 border rounded-lg bg-white/70 dark:bg-slate-800/60 border-white/20 dark:border-slate-700/40 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  min="0"
                  // Paired with the LABOR_RATE_MAX check in validateFinancialSettings. The
                  // attribute alone is not the guard — it only constrains the spinner and
                  // native validation, and a paste or an autofill walks straight past it.
                  max={LABOR_RATE_MAX}
                  step="0.01"
                  placeholder="75.00"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Continue Button */}
      <div className="text-center mt-8">
        <button
          onClick={saveFinancialSettings}
          disabled={saving}
          className="inline-flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white font-medium rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              Saving...
            </>
          ) : (
            <>
              <CheckCircleIcon className="w-5 h-5" />
              Save Financial Settings & Continue
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default FinancialSetupStep;
